import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Lock, Eye, ChevronRight, ChevronLeft, ExternalLink,
  MessageSquare, Send, Globe, Shield, Stethoscope,
  Tractor, Calculator, Rocket, Check, X, ArrowRight, Cpu, Flag, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'zambia-roundtable';

const projectIcons = {
  swiftguard: Shield,
  care365: Stethoscope,
  totalfarm: Tractor,
  finovo: Calculator,
  smartfarmbox: Cpu,
};

const projectColors = {
  swiftguard: { primary: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' },
  care365: { primary: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)' },
  smartfarmbox: { primary: '#22c55e', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.2)' },
  finovo: { primary: '#3b82f6', bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)' },
  totalfarm: { primary: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
};

const stageStyles = {
  'Ready to Go Live': { bg: 'rgba(16,185,129,0.15)', color: '#34d399', border: 'rgba(16,185,129,0.3)' },
  'Ready for Live Testing': { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  'Ready for Pilot Stage': { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  'Live': { bg: 'rgba(16,185,129,0.2)', color: '#22c55e', border: 'rgba(16,185,129,0.4)' },
};

const teamFlags = {
  sweden: { label: 'Team Sweden', bg: '#1e40af', flag: '\ud83c\uddf8\ud83c\uddea' },
  zambia: { label: 'Team Zambia', bg: '#16a34a', flag: '\ud83c\uddff\ud83c\uddf2' },
};

const ZambiaRoundtablePage = () => {
  const [phase, setPhase] = useState('password'); // password | viewing
  const [password, setPassword] = useState('');
  const [viewer, setViewer] = useState('');
  const [briefing, setBriefing] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ name: '', comment: '', project_slug: null });
  const [showCommentForm, setShowCommentForm] = useState(false);

  const totalSlides = briefing ? briefing.projects.length + 2 : 0; // intro + projects + future

  useEffect(() => {
    if (phase === 'viewing') fetchComments();
  }, [phase]);

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roundtable/comments/${SLUG}`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (e) { console.error(e); }
  };

  const handleAccess = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter the password'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roundtable/access/${SLUG}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        setBriefing(data.briefing);
        setViewer(data.viewer || '');
        setPhase('viewing');
        toast.success(`Welcome${data.viewer ? ', ' + data.viewer : ''}!`);
      } else {
        toast.error('Incorrect password');
      }
    } catch { toast.error('Network error'); }
    finally { setLoading(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name || !commentForm.comment) { toast.error('Please fill in all fields'); return; }
    try {
      const res = await fetch(`${API_URL}/api/roundtable/comment/${SLUG}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commenter_name: commentForm.name,
          comment: commentForm.comment,
          project_slug: commentForm.project_slug,
        }),
      });
      if (res.ok) {
        toast.success('Comment submitted');
        setCommentForm({ name: '', comment: '', project_slug: null });
        setShowCommentForm(false);
        fetchComments();
      }
    } catch { toast.error('Failed to submit comment'); }
  };

  // ─── Password Gate ───
  if (phase === 'password') {
    return (
      <div data-testid="roundtable-password-page" className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0c1220 0%, #1a2540 50%, #0c1220 100%)' }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md mx-4 p-10 rounded-2xl text-center"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1e40af, #7c3aed)' }}>
            <Lock size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Confidential Briefing</h1>
          <p className="text-gray-400 text-sm mb-8">Zambia Investment & Partnership Roundtable</p>
          <form onSubmit={handleAccess} className="space-y-4">
            <input
              data-testid="roundtable-password-input"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your access password"
              required
              className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <button
              data-testid="roundtable-access-btn" type="submit" disabled={loading}
              className="w-full py-3.5 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #1e40af, #7c3aed)' }}
            >
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Eye size={18} /> View Briefing</>}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ─── Presentation Viewer ───
  const project = currentSlide > 0 && currentSlide <= briefing.projects.length ? briefing.projects[currentSlide - 1] : null;
  const isFuturePlans = currentSlide === totalSlides - 1;
  const isIntro = currentSlide === 0;

  return (
    <div data-testid="roundtable-viewer" className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0c1220 0%, #1a2540 50%, #0c1220 100%)' }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(12,18,32,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-3">
          <Globe size={18} className="text-blue-400" />
          <span className="text-white text-sm font-medium">{briefing.title}</span>
          {viewer && <span className="text-xs px-2.5 py-1 rounded-full text-blue-200" style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)' }}>Viewing as {viewer}</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-xs">Slide {currentSlide + 1} / {totalSlides}</span>
          <button data-testid="roundtable-comment-toggle" onClick={() => setShowCommentForm(!showCommentForm)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-300 hover:text-white transition-all" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <MessageSquare size={14} /> Comment
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 w-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
        <motion.div animate={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }} className="h-full" style={{ background: 'linear-gradient(90deg, #1e40af, #7c3aed)' }} />
      </div>

      {/* Comment Panel */}
      <AnimatePresence>
        {showCommentForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="max-w-2xl mx-auto px-4 py-4">
              <form onSubmit={submitComment} className="flex flex-col sm:flex-row gap-3">
                <input data-testid="roundtable-comment-name" type="text" placeholder="Your name" value={commentForm.name} onChange={(e) => setCommentForm(p => ({ ...p, name: e.target.value }))} required className="px-3 py-2 rounded-lg text-white text-sm placeholder-gray-500 outline-none flex-shrink-0 sm:w-40" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }} />
                <input data-testid="roundtable-comment-text" type="text" placeholder="Leave a comment or feedback..." value={commentForm.comment} onChange={(e) => setCommentForm(p => ({ ...p, comment: e.target.value }))} required className="px-3 py-2 rounded-lg text-white text-sm placeholder-gray-500 outline-none flex-1" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }} />
                <button data-testid="roundtable-comment-submit" type="submit" className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5" style={{ background: 'linear-gradient(135deg, #1e40af, #7c3aed)' }}>
                  <Send size={14} /> Send
                </button>
              </form>
              {comments.length > 0 && (
                <div className="mt-3 space-y-2 max-h-32 overflow-y-auto">
                  {comments.slice(0, 5).map(c => (
                    <div key={c.id} className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <span className="text-blue-300 font-medium">{c.commenter_name}</span>
                      <span className="text-gray-500 mx-1.5">-</span>
                      <span className="text-gray-300">{c.comment}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slide Content */}
      <div className="max-w-6xl mx-auto px-6 py-8 sm:py-10 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
        <AnimatePresence mode="wait">
          {isIntro && (
            <motion.div key="intro" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
              <IntroSlide briefing={briefing} onProjectClick={(i) => setCurrentSlide(i + 1)} />
            </motion.div>
          )}
          {project && (
            <motion.div key={project.slug} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
              <ProjectSlide project={project} index={currentSlide - 1} totalProjects={briefing.projects.length} viewer={viewer} />
            </motion.div>
          )}
          {isFuturePlans && (
            <motion.div key="future" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
              <FuturePlansSlide plans={briefing.future_plans} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 px-4 py-4 flex items-center justify-between" style={{ background: 'rgba(12,18,32,0.95)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          data-testid="roundtable-prev-slide"
          onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-30 transition-all hover:brightness-110"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <ChevronLeft size={16} /> Previous
        </button>

        {/* Dots */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSlides }, (_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)}
              className="w-2.5 h-2.5 rounded-full transition-all"
              style={{ background: i === currentSlide ? '#7c3aed' : 'rgba(255,255,255,0.15)', transform: i === currentSlide ? 'scale(1.3)' : 'scale(1)' }}
            />
          ))}
        </div>

        <button
          data-testid="roundtable-next-slide"
          onClick={() => setCurrentSlide(s => Math.min(totalSlides - 1, s + 1))}
          disabled={currentSlide === totalSlides - 1}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-30 transition-all hover:brightness-110"
          style={{ background: 'linear-gradient(135deg, #1e40af, #7c3aed)' }}
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Slide Components ───

const IntroSlide = ({ briefing, onProjectClick }) => (
  <div className="text-center">
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)' }}>
      <Globe size={16} className="text-purple-400" />
      <span className="text-purple-300 text-sm font-medium">Confidential Briefing</span>
    </div>
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{briefing.title}</h1>
    <h2 className="text-base sm:text-lg text-blue-300 font-medium mb-8">{briefing.subtitle}</h2>
    <p className="text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">{briefing.intro_text}</p>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
      {briefing.projects.map((p, i) => {
        const Icon = projectIcons[p.slug] || Globe;
        const colors = projectColors[p.slug] || { primary: '#7c3aed', bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.2)' };
        const sStyle = p.stage ? (stageStyles[p.stage] || stageStyles['Live']) : null;
        return (
          <button key={p.slug} onClick={() => onProjectClick(i)} className="p-4 rounded-xl text-center transition-all hover:scale-105 hover:brightness-125 cursor-pointer" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
            <Icon size={24} className="mx-auto mb-2" style={{ color: colors.primary }} />
            <div className="text-white text-sm font-medium">{p.title.split(' - ')[0].split('.')[0]}</div>
            {sStyle && <div className="text-xs font-semibold mt-2 px-2 py-1 rounded-full inline-block" style={{ background: sStyle.bg, color: sStyle.color, border: `1px solid ${sStyle.border}` }}>{p.stage}</div>}
          </button>
        );
      })}
    </div>
  </div>
);

const ProjectSlide = ({ project, index, totalProjects, viewer }) => {
  const colors = projectColors[project.slug] || { primary: '#7c3aed', bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.2)' };
  const Icon = projectIcons[project.slug] || Globe;
  return (
    <div className="pb-20">
      {/* Hero Image - Full Width */}
      <div className="w-full h-56 sm:h-72 lg:h-80 rounded-2xl overflow-hidden mb-8 relative" style={{ border: `1px solid ${colors.border}` }}>
        <img src={project.image_url} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(12,18,32,0.8) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: colors.primary }}>
              <Icon size={22} className="text-white" />
            </div>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full text-white" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>Project {index + 1} of {totalProjects}</span>
            {project.platform_url && (
              <a href={project.platform_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full text-white transition-all hover:brightness-125" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                <ExternalLink size={13} /> Visit Platform
              </a>
            )}
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1">{project.title}</h2>
          <p className="text-base font-medium" style={{ color: colors.primary }}>{project.tagline}</p>
        </div>
      </div>

      {/* Stage Badge + Team Flags + Job Creation */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {project.stage && (
          <span className="text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider" style={{ background: (stageStyles[project.stage] || stageStyles['Live']).bg, color: (stageStyles[project.stage] || stageStyles['Live']).color, border: `1px solid ${(stageStyles[project.stage] || stageStyles['Live']).border}` }}>
            {project.stage}
          </span>
        )}
        {project.teams && project.teams.map((t) => {
          const tf = teamFlags[t] || { label: t, bg: '#475569', flag: '' };
          return (
            <span key={t} className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full text-white" style={{ background: tf.bg }}>
              <Flag size={12} /> {tf.label}
            </span>
          );
        })}
        {project.job_creation && (
          <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full text-white" style={{ background: 'rgba(168,85,247,0.25)', border: '1px solid rgba(168,85,247,0.4)' }}>
            <Users size={12} className="text-purple-300" /> {project.job_creation.roles}: {project.job_creation.count}
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-gray-300 text-base leading-relaxed mb-8 max-w-4xl">{project.description}</p>

      {/* Key Features - Horizontal Chips */}
      {project.key_features && project.key_features.length > 0 && (
        <div className="mb-8">
          <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider">Key Capabilities</h4>
          <div className="flex flex-wrap gap-2.5">
            {project.key_features.map((f, i) => (
              <span key={i} className="flex items-center gap-2 text-sm px-4 py-2 rounded-full text-gray-200" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                <Check size={14} style={{ color: colors.primary }} /> {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Info Grid - 2x2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InfoCard title="Current Discussion Status" text={project.discussion_status} color={colors.primary} bg={colors.bg} border={colors.border} />
        <InfoCard title="Government Support Needed" text={project.help_needed} color={colors.primary} bg={colors.bg} border={colors.border} />
        <InfoCard title="Investment Made" text={project.investment_info} color={colors.primary} bg={colors.bg} border={colors.border} />
        <InfoCard title="Revenue Model & Opportunity" text={project.revenue_model} color={colors.primary} bg={colors.bg} border={colors.border} />
      </div>

      {/* Personalized Discussion Points */}
      {viewer && project.viewer_questions && project.viewer_questions[viewer] && (
        <div className="mt-6 p-5 sm:p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(30,64,175,0.12), rgba(124,58,237,0.12))', border: '1px solid rgba(124,58,237,0.2)' }}>
          <h5 className="text-sm font-bold mb-4 text-purple-300 uppercase tracking-wider flex items-center gap-2">
            <MessageSquare size={16} /> Discussion Points for {viewer}
          </h5>
          <ul className="space-y-2.5">
            {project.viewer_questions[viewer].map((q, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-200 leading-relaxed">
                <ChevronRight size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ title, text, color, bg, border }) => (
  <div className="p-5 sm:p-6 rounded-xl" style={{ background: bg || 'rgba(255,255,255,0.03)', border: `1px solid ${border || 'rgba(255,255,255,0.06)'}` }}>
    <h5 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color }}>{title}</h5>
    <p className="text-gray-300 text-sm leading-relaxed">{text}</p>
  </div>
);

const FuturePlansSlide = ({ plans }) => (
  <div className="text-center pb-16">
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)' }}>
      <Rocket size={16} className="text-purple-400" />
      <span className="text-purple-300 text-sm font-medium">Looking Ahead</span>
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Future Plans & Opportunities</h2>
    <p className="text-gray-400 max-w-xl mx-auto mb-10 text-sm">Our roadmap for continued growth, partnership, and impact across Zambia and the SADC region.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-left">
      {plans.map((p, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #1e40af, #7c3aed)' }}>{i + 1}</div>
            <h4 className="text-white text-sm font-semibold">{p.title}</h4>
          </div>
          <p className="text-gray-400 text-xs leading-relaxed">{p.description}</p>
        </motion.div>
      ))}
    </div>
    <div className="mt-12 p-6 rounded-xl max-w-xl mx-auto" style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)' }}>
      <p className="text-purple-200 text-sm">We look forward to discussing how we can move forward together. Your insights and support are invaluable to making these projects a success for Zambia.</p>
      <p className="text-gray-400 text-xs mt-3">Contact: sales@voxitconsulting.media</p>
    </div>
  </div>
);

export default ZambiaRoundtablePage;
